/* Page FAQs — the client's screen.

   Two views. The picker lists their sitemap as pages a person recognises
   ("About Us › Meet the Team"), grouped into sections, with counts so the work
   is visible before anything is opened. The editor is one page's Q&As.

   The thing this screen has to keep honest is WHERE AN ANSWER ACTUALLY IS. A
   Q&A can be in the Vault but not on the page, on the page but waiting for
   review, or approved and ticked but not yet saved to the site. Each of those
   is a different sentence to the client, and collapsing them into "saved" is
   how someone believes their answer is live when it is not. */
import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { htmlToMarkdown, markdownToHtml } from '@/lib/faqMarkdown';
import { normalizeLink } from '@/components/RichTextEditor';
import { Icon } from '@/lib/icons';
import { ExpertPicker } from '@/components/ExpertPicker';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { useDictation, MicButton } from '@/lib/dictation';
import {
  getFaqPages, getFaqPage, addPageFaq, editPageFaq, setFaqPageOrder, removeFaqFromPage,
  approvePageFaq, publishFaqPage, askFaqPage, suggestFaqPage, checkFaqPage, similarFaqs, type PageFaq,
} from '@/data/client';

const errMsg = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

export function FaqPages({ onBack, onGeneral }: { onBack: () => void; onGeneral: () => void }) {
  const [route, setRoute] = useState<string | null>(null);
  return route
    ? <FaqPageEditor route={route} onBack={() => setRoute(null)} />
    : <FaqPagePicker onBack={onBack} onPick={setRoute} onGeneral={onGeneral} />;
}

/* ---- the picker ------------------------------------------------------- */

function FaqPagePicker({ onBack, onPick, onGeneral }: { onBack: () => void; onPick: (route: string) => void; onGeneral: () => void }) {
  const { data, isLoading } = useQuery({ queryKey: ['faqPages'], queryFn: getFaqPages });

  if (isLoading) return <p className="dash-muted">Loading your pages…</p>;

  /* Without the sitemap there are no pages to choose, and an empty list with no
     explanation reads as broken rather than as not-set-up-yet. */
  if (data?.needsSitemap) {
    return (
      <div className="dash-stack">
        <BackLink onClick={onBack} />
        <Empty icon="map-pin" title="No pages yet">
          Your site map hasn’t been imported, so there are no pages to add FAQs to.
          Ask your Triton contact to import it — it takes a minute.
        </Empty>
      </div>
    );
  }

  return (
    <div className="dash-stack">
      <BackLink onClick={onBack} />
      <div>
        <h2 className="faqp__h">Questions for your pages</h2>
        <p className="dash-muted">
          Answer the questions your customers actually ask. Your answers are used to write your
          content in your own words — and the best of them can appear on the page itself.
        </p>
      </div>

      {/* Questions that belong to the business rather than to one page. These
          predate page FAQs, so the entry has to stay or a client's existing
          answers become uneditable. */}
      <Card padding="lg">
        <button type="button" className="faqp__page" onClick={onGeneral}>
          <span className="faqp__name">General questions</span>
          <span className="faqp__counts"><span className="faqp__none">About your business, not one page</span></span>
          <Icon name="chevron-right" size={16} />
        </button>
      </Card>

      {(data?.sections ?? []).map((sec) => (
        <Card key={sec.name} padding="lg">
          <h3 className="faqp__sec">{sec.name}</h3>
          <ul className="faqp__pages">
            {sec.pages.map((p) => (
              <li key={p.route}>
                <button type="button" className="faqp__page" onClick={() => onPick(p.route)} title={p.route}>
                  <span className="faqp__name">{p.name}</span>
                  <span className="faqp__counts">
                    {p.total === 0
                      ? <span className="faqp__none">No questions yet</span>
                      : <>
                          <span>{p.total} question{p.total === 1 ? '' : 's'}</span>
                          {p.shown > 0 && <Badge variant="success">{p.shown} on page</Badge>}
                          {p.waiting > 0 && <Badge variant="warning" dot>{p.waiting} awaiting review</Badge>}
                        </>}
                  </span>
                  <Icon name="chevron-right" size={16} />
                </button>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}

/* ---- one page --------------------------------------------------------- */

function FaqPageEditor({ route, onBack }: { route: string; onBack: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['faqPage', route], queryFn: () => getFaqPage(route) });
  const [q, setQ] = useState('');
  const [a, setA] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState('');

  const [editing, setEditing] = useState<{ id: string; q: string; a: string } | null>(null);
  /* Checked when the question loses focus, not on save: a warning that arrives
     as the save completes is a warning nobody can act on. Flags only — the
     save is never blocked, because sometimes a near-duplicate is a deliberate
     update and a refused save loses what was typed. */
  const [dupes, setDupes] = useState<{ id: string; question: string; answer: string; why: string }[]>([]);
  const checkDupes = async () => {
    if (!q.trim()) { setDupes([]); return; }
    try { setDupes((await similarFaqs(q)).matches); } catch { setDupes([]); }
  };
  const items = data?.items ?? [];
  const shown = items.filter((i) => i.display && i.state === 'approved').length;

  /* Whose answer this is. Only needed when Triton types it from a call — a
     client answering in their own login is resolved server-side from who they
     are, with nothing to remember. */
  const [answeredBy, setAnsweredBy] = useState('');

  const refresh = (next: PageFaq[]) =>
    qc.setQueryData(['faqPage', route], (d: typeof data) => d && { ...d, items: next, pending: true });

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true); setErr(''); setSaved('');
    try { await fn(); } catch (e) { setErr(errMsg(e, fallback)); } finally { setBusy(false); }
  };

  const add = () => run(async () => {
    const r = await addPageFaq(route, { question: q, answer: a }, answeredBy || undefined);
    refresh(r.items); setQ(''); setA(''); setDupes([]);
    qc.invalidateQueries({ queryKey: ['faqPages'] });
  }, 'Could not save that question.');

  const toggle = (id: string) => run(async () => {
    const next = items.map((i) => ({ id: i.id, display: i.id === id ? !i.display : i.display }));
    refresh((await setFaqPageOrder(route, next)).items);
  }, 'Could not change that.');

  const move = (i: number, by: number) => run(async () => {
    const next = [...items];
    const to = i + by;
    if (to < 0 || to >= next.length) return;
    [next[i], next[to]] = [next[to], next[i]];
    refresh((await setFaqPageOrder(route, next.map((x) => ({ id: x.id, display: x.display })))).items);
  }, 'Could not reorder.');

  const saveEdit = () => run(async () => {
    if (!editing) return;
    const r = await editPageFaq(route, editing.id, { question: editing.q, answer: editing.a });
    qc.setQueryData(['faqPage', route], (d: typeof data) => d && { ...d, items: r.items, pending: r.pending });
    setEditing(null);
  }, 'Could not save that change.');

  const publish = () => run(async () => {
    const r = await publishFaqPage(route);
    setSaved(r.unchanged ? 'Nothing had changed — your site is already up to date.' : `Saved ${r.count} question${r.count === 1 ? '' : 's'} to your site.`);
    qc.invalidateQueries({ queryKey: ['faqPage', route] });
  }, 'Could not save to your site.');

  if (isLoading || !data) return <p className="dash-muted">Loading…</p>;

  return (
    <div className="dash-stack">
      <BackLink onClick={onBack} label="All pages" />
      <div className="faqp__head">
        <div>
          <h2 className="faqp__h">{data.label}</h2>
          <p className="dash-muted">
            {items.length} question{items.length === 1 ? '' : 's'} saved
            {shown > 0 && <> · <strong>{shown}</strong> chosen for the page</>}
          </p>
        </div>
        {data.pushEnabled ? (
          <div className="faqp__save">
            {data.pending && <span className="faqp__pending">Changes not yet on your site</span>}
            <Button variant="primary" disabled={busy || !data.pending} onClick={publish}>
              {busy ? 'Saving…' : 'Save to site'}
            </Button>
          </div>
        ) : (
          /* Said plainly rather than hiding the ticks: a client who selects
             questions and sees nothing happen concludes it is broken. */
          <p className="faqp__notready">
            <Icon name="alert-circle" size={14} /> Your FAQ page isn’t set up yet — your answers are
            saved and used for your content, and we’ll put the chosen ones on the page once it’s ready.
          </p>
        )}
      </div>

      {shown > 8 && (
        <p className="faqp__warn">
          <Icon name="alert-circle" size={14} /> {shown} questions on one page is a lot — long FAQ
          blocks push your real content down. Six to eight usually reads best.
        </p>
      )}

      {data.canApprove && <AskPanel route={route} label={data.label} />}

      <Card padding="lg">
        <h3 className="faqp__sec">Add a question</h3>
        <div className="faqp__form">
          <input className="faqp__q" placeholder="What do customers ask?" value={q}
            onChange={(e) => { setQ(e.target.value); if (dupes.length) setDupes([]); }}
            onBlur={checkDupes} />
          {dupes.map((d) => (
            <div key={d.id} className="faqp__dupe">
              <p className="faqp__dupe__h">
                <Icon name="alert-circle" size={13} /> You’ve answered something close{d.why ? ` — ${d.why}` : ''}
              </p>
              <p className="faqp__dupe__q">{d.question}</p>
              <p className="faqp__dupe__a">{d.answer}</p>
            </div>
          ))}
          {dupes.length > 0 && (
            <p className="dash-muted" style={{ margin: 0, fontSize: 'var(--text-caption)' }}>
              Save anyway if this is different, or if you’re updating what you said before.
            </p>
          )}
          <AnswerField value={a} onChange={setA} rows={4} placeholder="Answer in your own words…" disabled={busy} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Button variant="primary" disabled={busy || !q.trim() || !a.trim()} onClick={add}>Save question</Button>
            <ExpertPicker value={answeredBy} onChange={setAnsweredBy} label="Answered by" disabled={busy} />
          </div>
        </div>
      </Card>

      {err && <p className="faqp__err">{err}</p>}
      {saved && <p className="faqp__ok">{saved}</p>}

      {items.length === 0 ? (
        <Empty icon="help-circle" title="No questions for this page yet">
          Add the questions your customers ask most about this service.
        </Empty>
      ) : (
        <Card padding="lg">
          <h3 className="faqp__sec">Saved questions</h3>
          <ul className="faqp__list">
            {items.map((it, i) => (
              <li key={it.id} className={it.display ? 'is-shown' : ''}>
                <label className="faqp__tick">
                  <input type="checkbox" checked={it.display} disabled={busy} onChange={() => toggle(it.id)} />
                  <span>Show on page</span>
                </label>
                {editing?.id === it.id ? (
                  <div className="faqp__qa faqp__form">
                    <input className="faqp__q" value={editing.q} onChange={(e) => setEditing({ ...editing, q: e.target.value })} />
                    <AnswerField value={editing.a} onChange={(v) => setEditing({ ...editing, a: v })} rows={4} disabled={busy} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button variant="primary" size="sm" disabled={busy || !editing.q.trim() || !editing.a.trim()} onClick={saveEdit}>Save</Button>
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => setEditing(null)}>Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <div className="faqp__qa">
                    <strong>{it.question}</strong>
                    <AnswerText text={it.answer} />
                    {it.state === 'awaiting_review' && (
                      <Badge variant="warning" dot>Waiting for Triton to review</Badge>
                    )}
                  </div>
                )}
                <div className="faqp__rowacts">
                  <button type="button" className="faqp__icon" disabled={busy || !!editing} aria-label="Edit"
                    onClick={() => setEditing({ id: it.id, q: it.question, a: it.answer })}>
                    <Icon name="edit" size={15} />
                  </button>
                  {data.canApprove && it.state === 'awaiting_review' && (
                    <Button variant="ghost" size="sm" disabled={busy}
                      onClick={() => run(async () => refresh((await approvePageFaq(route, it.id)).items), 'Could not approve.')}>
                      Approve
                    </Button>
                  )}
                  <button type="button" className="faqp__icon" disabled={busy || i === 0} onClick={() => move(i, -1)} aria-label="Move up">
                    <Icon name="chevron-up" size={15} />
                  </button>
                  <button type="button" className="faqp__icon" disabled={busy || i === items.length - 1} onClick={() => move(i, 1)} aria-label="Move down">
                    <Icon name="chevron-down" size={15} />
                  </button>
                  {/* Off this page, not deleted: the answer still grounds their
                      content and may be on another page. */}
                  <button type="button" className="faqp__icon" disabled={busy} aria-label="Remove from this page"
                    onClick={() => run(async () => refresh((await removeFaqFromPage(route, it.id)).items), 'Could not remove.')}>
                    <Icon name="x" size={15} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

/* Triton-only. Asking a client questions spends their attention, so it is not
   something the client does to themselves — and the answers come back through
   the same email, phone screen and weekly reminders the blog questions use. */
function AskPanel({ route, label }: { route: string; label: string }) {
  /* ONE list of candidate questions, whatever put them there. Suggestions and
     typed questions used to live in different places — a textarea, a read-only
     list below it, and a send button that could only take all of them — which
     meant the one thing you actually want to do, strike a weak question before
     it reaches the client, was the one thing you could not. */
  const [rows, setRows] = useState<AskRow[]>([]);
  const [bulk, setBulk] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [sent, setSent] = useState<{ n: number; drafted: number } | null>(null);
  const [checking, setChecking] = useState(false);
  const [err, setErr] = useState('');
  /* Who we are asking. Captured at send time, which is the only moment it is
     cheap to know — afterwards the answers are in the Vault unattributed. */
  const [expertId, setExpertId] = useState('');

  const picked = rows.filter((r) => r.on && r.text.trim());
  const patch = (id: string, p: Partial<AskRow>) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...p } : r)));
  const addRows = (items: { text: string; why?: string }[]) =>
    setRows((rs) => [...rs, ...items.map((i) => ({ id: crypto.randomUUID(), text: i.text, why: i.why ?? '', on: true }))]);

  /* Appended and ticked, never replacing what is already there: losing a
     question someone typed to make room for a suggestion is unforgivable. */
  const suggest = async () => {
    setThinking(true); setErr('');
    try {
      const r = await suggestFaqPage(route);
      if (!r.questions.length) { setErr('Nothing new to suggest — the Vault may already cover this page.'); return; }
      addRows(r.questions.map((q) => ({ text: q.q, why: q.why })));
    } catch (e) { setErr(errMsg(e, 'Could not suggest questions.')); }
    finally { setThinking(false); }
  };

  /* The same pass the send does, run on demand. Shows what is already on
     record so a question can be dropped before it costs the client anything —
     the send reports this afterwards, which is too late to act on. */
  const check = async () => {
    const asking = rows.filter((r) => r.text.trim());
    if (!asking.length) return;
    setChecking(true); setErr('');
    try {
      const r = await checkFaqPage(route, asking.map((a) => a.text.trim()));
      const byQ = new Map(r.answers.map((a) => [a.question, a]));
      setRows((rs) => rs.map((row) => {
        const hit = byQ.get(row.text.trim());
        return hit ? { ...row, checked: true, found: hit.answer, foundSource: hit.source } : row;
      }));
    } catch (e) { setErr(errMsg(e, 'Could not check the Vault.')); }
    finally { setChecking(false); }
  };

  const takeBulk = () => {
    const lines = bulk.split('\n').map((l) => l.trim()).filter(Boolean);
    if (!lines.length) return;
    addRows(lines.map((text) => ({ text })));
    setBulk(''); setBulkOpen(false);
  };

  const send = async () => {
    setBusy(true); setErr('');
    try {
      const r = await askFaqPage(route, picked.map((p) => p.text.trim()), expertId || undefined);
      setSent({ n: r.sent, drafted: r.drafted ?? 0 });
      setRows([]);
    } catch (e) { setErr(errMsg(e, 'Could not send those questions.')); }
    finally { setBusy(false); }
  };

  if (sent) {
    return (
      <Card padding="lg">
        <p className="faqp__ok">
          Sent {sent.n} question{sent.n === 1 ? '' : 's'}. Answers arrive here as FAQs for {label}, and
          the client is reminded weekly until they reply.
        </p>
        {sent.drafted > 0 && (
          <p className="dash-muted" style={{ margin: '8px 0 0' }}>
            {sent.drafted} already had an answer in the Vault — those go across as a draft to confirm
            rather than asked from scratch.
          </p>
        )}
        <div style={{ marginTop: 14 }}>
          <Button variant="outline" size="sm" onClick={() => setSent(null)}>Ask more questions</Button>
        </div>
      </Card>
    );
  }

  return (
    <Card padding="lg">
      <div className="faqp__askhead">
        <h3 className="faqp__sec">Ask the client</h3>
        <div className="faqp__askbtns">
          <Button variant="outline" size="sm" disabled={thinking} onClick={suggest}>
            {thinking ? 'Reading the page…' : 'Suggest questions'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => addRows([{ text: '' }])}>Add your own</Button>
          {rows.some((r) => r.text.trim()) && (
            <Button variant="outline" size="sm" disabled={checking} onClick={check}>
              {checking ? 'Checking…' : 'Check the Vault'}
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setBulkOpen((v) => !v)}>
            {bulkOpen ? 'Cancel' : 'Paste a list'}
          </Button>
        </div>
      </div>

      {bulkOpen && (
        <div className="faqp__form" style={{ marginBottom: 14 }}>
          <textarea className="faqp__a" rows={6} value={bulk} onChange={(e) => setBulk(e.target.value)}
            placeholder={'One question per line…'} />
          <Button variant="primary" size="sm" disabled={!bulk.trim()} onClick={takeBulk}>Add these</Button>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="dash-muted" style={{ margin: 0 }}>
          Send questions for this page and the client answers them on their phone. Answers come back
          here as FAQs, and into the Vault where they ground everything we write.
        </p>
      ) : (
        <>
          {/* Editable in place, ticked by default, struck individually. */}
          <ul className="faqp__ask">
            {rows.map((r) => (
              <li key={r.id} className={r.on ? '' : 'is-off'}>
                <input type="checkbox" checked={r.on} onChange={() => patch(r.id, { on: !r.on })}
                  aria-label={r.on ? 'Do not send this question' : 'Send this question'} />
                <div className="faqp__askq">
                  <input value={r.text} placeholder="Type a question…" onChange={(e) => patch(r.id, { text: e.target.value })} />
                  {r.why && <span className="faqp__askwhy">{r.why}</span>}
                  {/* Already on record. Shown with its source and the option to
                      drop it, because asking again is what makes a client stop
                      reading these. Kept sendable: the client confirming their
                      own words is still worth something. */}
                  {r.found ? (
                    <div className="faqp__found">
                      <p className="faqp__found__h">
                        <Icon name="check-circle" size={13} /> Already answered{r.foundSource ? ` — ${r.foundSource}` : ''}
                      </p>
                      <p className="faqp__found__a">{r.found}</p>
                      <div className="faqp__found__acts">
                        <button type="button" onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))}>
                          Don’t ask this
                        </button>
                        <span>·</span>
                        <button type="button" onClick={() => patch(r.id, { on: true })}>
                          Send it to confirm
                        </button>
                      </div>
                    </div>
                  ) : r.checked ? (
                    <span className="faqp__askwhy">Not in the Vault — worth asking</span>
                  ) : null}
                </div>
                <button type="button" className="faqp__icon" aria-label="Remove this question"
                  onClick={() => setRows((rs) => rs.filter((x) => x.id !== r.id))}>
                  <Icon name="x" size={15} />
                </button>
              </li>
            ))}
          </ul>

          {err && <p className="faqp__err">{err}</p>}
          <div className="faqp__asksend">
            <ExpertPicker value={expertId} onChange={setExpertId} disabled={busy} />
            <Button variant="primary" disabled={busy || !picked.length} onClick={send}>
              {busy ? 'Sending…' : `Send ${picked.length} question${picked.length === 1 ? '' : 's'}`}
            </Button>
            {rows.length !== picked.length && (
              <span className="dash-muted">{rows.length - picked.length} not being sent</span>
            )}
          </div>
        </>
      )}
      {rows.length === 0 && err && <p className="faqp__err">{err}</p>}
    </Card>
  );
}

interface AskRow {
  id: string; text: string; why: string; on: boolean;
  /** What the Vault already says, once checked. Empty means nothing on record. */
  found?: string;
  foundSource?: string;
  checked?: boolean;
}

/* The answer box: bold, italic, lists and links, with dictation.

   It edits Markdown (see lib/faqMarkdown) through a formatted view, so nobody
   types `**` or `[text](/page)` — and no headings, because an answer already
   sits under its question's heading. contentEditable rather than an editor
   library for the reason RichTextEditor gives: a library can produce things
   the Markdown cannot carry, and they would vanish on save.

   A link is a page from the same sitemap the picker shows — the only internal
   links the save-time check accepts — or a web address / email. Pages are
   written without a trailing slash, the form the sites' own links use. Paste
   is plain text, so formatting from Word or a website can't sneak in. */
export function AnswerField({ value, onChange, rows, placeholder, disabled }: {
  value: string; onChange: (v: string) => void; rows: number; placeholder?: string; disabled?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // What this editor last emitted: the DOM is rewritten only when `value`
  // changes from outside (dictation, a cleared form), or the caret jumps.
  const emitted = useRef<string | null>(null);
  const saved = useRef<Range | null>(null);
  const [state, setState] = useState({ bold: false, italic: false, list: '' });
  const [linking, setLinking] = useState(false);
  const [url, setUrl] = useState('');
  const [linkErr, setLinkErr] = useState('');
  const { listening, micError, pending, level, start, stop } = useDictation(onChange);
  const { data } = useQuery({ queryKey: ['faqPages'], queryFn: getFaqPages, enabled: linking });
  const pages = (data?.sections ?? []).flatMap((s) => s.pages);

  useEffect(() => {
    const el = ref.current;
    if (!el || value === emitted.current) return;
    el.innerHTML = markdownToHtml(value);
    emitted.current = value;
  }, [value]);

  const emit = () => {
    if (!ref.current) return;
    const md = htmlToMarkdown(ref.current);
    emitted.current = md;
    onChange(md);
  };

  const inside = () => {
    const sel = window.getSelection();
    return sel && sel.rangeCount > 0 && ref.current?.contains(sel.anchorNode) ? sel : null;
  };

  useEffect(() => {
    const update = () => {
      const sel = inside();
      if (!sel) return;
      const li = (sel.anchorNode instanceof Element ? sel.anchorNode : sel.anchorNode?.parentElement)?.closest('li');
      setState({
        bold: document.queryCommandState('bold'),
        italic: document.queryCommandState('italic'),
        list: li && ref.current?.contains(li) ? li.parentElement!.tagName.toLowerCase() : '',
      });
    };
    document.addEventListener('selectionchange', update);
    return () => document.removeEventListener('selectionchange', update);
  }, []);

  const run = (cmd: string, arg?: string) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const startLink = () => {
    const sel = inside();
    saved.current = sel ? sel.getRangeAt(0).cloneRange() : null;
    setUrl(''); setLinkErr(''); setLinking(true);
  };

  const applyLink = (href: string, fallbackText: string) => {
    ref.current?.focus();
    const sel = window.getSelection();
    if (sel && saved.current) { sel.removeAllRanges(); sel.addRange(saved.current); }
    if (sel && !sel.isCollapsed && ref.current?.contains(sel.anchorNode)) {
      document.execCommand('createLink', false, href);
    } else {
      // Nothing selected: the page's name (or the address) becomes the link text.
      const esc = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
      document.execCommand('insertHTML', false, `<a href="${esc(href)}">${esc(fallbackText)}</a>&nbsp;`);
    }
    setLinking(false);
    emit();
  };

  const linkPage = (route: string) => {
    const p = pages.find((x) => x.route === route);
    if (p) applyLink(route === '/' ? '/' : route.replace(/\/+$/, ''), p.name);
  };
  const linkUrl = () => {
    const href = normalizeLink(url);
    if (!href) { setLinkErr('Enter a web address or an email.'); return; }
    applyLink(href, href.replace(/^mailto:/i, ''));
  };

  const btn = (label: React.ReactNode, title: string, on: boolean, act: () => void) => (
    <button type="button" className={`rte__btn${on ? ' is-on' : ''}`} title={title} aria-pressed={on} disabled={disabled}
      onMouseDown={(e) => { e.preventDefault(); act(); }}>
      {label}
    </button>
  );

  return (
    <div className="faqp__afield">
      <div className="faqp__rte rte">
        <div className="rte__bar" role="toolbar" aria-label="Answer formatting">
          {btn(<strong>B</strong>, 'Bold (Ctrl+B)', state.bold, () => run('bold'))}
          {btn(<em>I</em>, 'Italic (Ctrl+I)', state.italic, () => run('italic'))}
          <span className="rte__sep" />
          {btn(<Icon name="list" size={15} />, 'Bulleted list', state.list === 'ul', () => run('insertUnorderedList'))}
          {btn(<span className="rte__ol">1.</span>, 'Numbered list', state.list === 'ol', () => run('insertOrderedList'))}
          <span className="rte__sep" />
          {btn(<Icon name="link" size={15} />, 'Add link (select words first to make them the link)', false, startLink)}
          {btn(<><Icon name="link" size={15} /><Icon name="x" size={11} /></>, 'Remove link', false, () => run('unlink'))}
          <MicButton listening={listening} level={level} onToggle={() => (listening ? stop() : start(value))} />
        </div>
        {linking && (
          <div className="rte__link">
            <select className="faqp__linkpick" defaultValue="" disabled={!pages.length}
              onChange={(e) => linkPage(e.target.value)}>
              <option value="" disabled>{pages.length ? 'A page on your site…' : 'Loading pages…'}</option>
              {(data?.sections ?? []).map((s) => (
                <optgroup key={s.name} label={s.name}>
                  {s.pages.map((p) => <option key={p.route} value={p.route}>{p.label}</option>)}
                </optgroup>
              ))}
            </select>
            <span className="faqp__linkhint">or</span>
            <input className="tr-input" placeholder="https://… or name@example.com" value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); linkUrl(); }
                if (e.key === 'Escape') { e.preventDefault(); setLinking(false); }
              }} />
            <button type="button" className="rte__btn rte__btn--text" disabled={!url.trim()} onClick={linkUrl}>Add</button>
            <button type="button" className="rte__btn rte__btn--text" onClick={() => setLinking(false)}>Cancel</button>
            {linkErr && <span className="rte__err">{linkErr}</span>}
          </div>
        )}
        <div
          ref={ref}
          className="rte__area faqp__md"
          style={{ minHeight: `${rows * 1.55 + 1.3}em` }}
          contentEditable={!disabled && !pending}  // a phrase still transcribing would overwrite typing
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          aria-label="Answer"
          data-placeholder={placeholder}
          onFocus={() => document.execCommand('defaultParagraphSeparator', false, 'p')}
          onInput={emit}
          onKeyDown={(e) => {
            // Ctrl+U would underline, which Markdown can't carry.
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'u') e.preventDefault();
          }}
          onPaste={(e) => {
            e.preventDefault();
            document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
          }}
          // A link here is the client's site: following it would leave the page and the answer with it.
          onClick={(e) => { if ((e.target as Element).closest('a')) e.preventDefault(); }}
        />
      </div>
      {pending && !listening && <p className="dash-muted" style={{ marginTop: 6, fontSize: 'var(--text-caption)' }}>Transcribing…</p>}
      {micError && <p className="faqp__err" style={{ marginTop: 6, fontSize: 'var(--text-caption)' }}>{micError}</p>}
    </div>
  );
}

/** A stored answer as it will read on the page: formatting shown, links not followable. */
function AnswerText({ text }: { text: string }) {
  return <div className="faqp__md" dangerouslySetInnerHTML={{ __html: markdownToHtml(text, 'span') }} />;
}

function Empty({ icon, title, children }: { icon: string; title: string; children: React.ReactNode }) {
  return (
    <div className="dash-empty">
      <span className="dash-empty__icon"><Icon name={icon} size={26} /></span>
      <h2>{title}</h2>
      <p>{children}</p>
    </div>
  );
}

function BackLink({ onClick, label = 'Back' }: { onClick: () => void; label?: string }) {
  return (
    <button type="button" className="faqp__back" onClick={onClick}>
      <Icon name="chevron-left" size={15} /> {label}
    </button>
  );
}
